import type { ReactElement } from 'react'
import { MarketingLayout } from '../Marketing/MarketingLayout'
import styles from './TermsOfService.module.css'

export function TermsOfService(): ReactElement {
  return (
    <MarketingLayout>
      <div className={styles.maxWidth}>
        <header className={styles.header}>
          <h1 className={styles.title}>Terms of Service</h1>
          <div className={styles.date}>Effective Date: December 14, 2025</div>
        </header>

        <p className={styles.paragraph}>
          Welcome to aether studio (“Aether,” “we,” “us,” or “our”). These Terms of Service (“Terms”) govern your access to and use of our website, applications, tools, APIs, and related services (collectively, the “Services”).
        </p>

        <p className={styles.paragraph}>
          By accessing or using the Services, you agree to be bound by these Terms. If you do not agree, do not use the Services.
        </p>

        <hr style={{ margin: '2rem 0', border: 'none', borderTop: '1px solid var(--color-border)' }} />

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>1. Eligibility and Account Registration</h2>
          <p className={styles.paragraph}>
            You must be at least 18 years old (or the age of majority in your jurisdiction) to use the Services.
          </p>
          <p className={styles.paragraph}>When creating an account, you agree to:</p>
          <ul className={styles.list}>
            <li className={styles.listItem}>Provide accurate, current, and complete information.</li>
            <li className={styles.listItem}>Maintain the security of your credentials.</li>
            <li className={styles.listItem}>Be responsible for all activities that occur under your account.</li>
          </ul>
          <p className={styles.paragraph}>
            You may not share accounts except as explicitly permitted through team or enterprise features.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>2. The Services</h2>
          <p className={styles.paragraph}>
            aether studio provides creative tools for AI-powered image generation, editing, versioning, collaboration, and asset management. The Services may include experimental or beta features.
          </p>
          <p className={styles.paragraph}>
            We reserve the right to modify, suspend, or discontinue any part of the Services at any time.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>3. User Content</h2>
          
          <h3 className={styles.subSectionTitle}>3.1 Ownership</h3>
          <p className={styles.paragraph}>
            You retain ownership of all content you upload, create, or generate using the Services (“User Content”). aether studio does not claim ownership over your User Content.
          </p>

          <h3 className={styles.subSectionTitle}>3.2 License to Aether</h3>
          <p className={styles.paragraph}>
            You grant aether studio a limited, non-exclusive, worldwide, royalty-free license to host, store, process, reproduce, and display your User Content solely for the purpose of operating, improving, and providing the Services.
          </p>

          <h3 className={styles.subSectionTitle}>3.3 Responsibility</h3>
          <p className={styles.paragraph}>
            You are solely responsible for your User Content and represent that you have all necessary rights, permissions, and licenses to upload and use such content.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>4. AI-Generated Content</h2>
          
          <h3 className={styles.subSectionTitle}>4.1 Nature of AI Outputs</h3>
          <p className={styles.paragraph}>
            The Services use artificial intelligence systems that generate content probabilistically. Outputs may be inaccurate, incomplete, or unexpected.
          </p>

          <h3 className={styles.subSectionTitle}>4.2 Use of Outputs</h3>
          <p className={styles.paragraph}>
            You are responsible for reviewing and validating AI-generated outputs before any commercial or public use.
          </p>

          <h3 className={styles.subSectionTitle}>4.3 No Training Without Permission</h3>
          <p className={styles.paragraph}>
            aether studio does not use your private User Content to train shared or public AI models without your explicit permission.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>5. Licensed Identities, Models, and Assets</h2>
          <p className={styles.paragraph}>
            Some features may involve licensed identities, models, or creative assets subject to additional terms. You agree to comply with all applicable licensing restrictions.
          </p>
          <p className={styles.paragraph}>
            Unauthorized use, redistribution, or misuse of licensed assets is strictly prohibited.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>6. Acceptable Use</h2>
          <p className={styles.paragraph}>You agree not to:</p>
          <ul className={styles.list}>
            <li className={styles.listItem}>Violate any applicable laws or regulations.</li>
            <li className={styles.listItem}>Infringe intellectual property, privacy, or publicity rights.</li>
            <li className={styles.listItem}>Upload content you do not have rights to use.</li>
            <li className={styles.listItem}>Use the Services to create or distribute harmful, deceptive, or illegal content.</li>
            <li className={styles.listItem}>Attempt to reverse engineer, scrape, or abuse the Services or underlying models.</li>
            <li className={styles.listItem}>Circumvent usage limits, credit systems, or security mechanisms.</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>7. Payments, Credits, and Subscriptions</h2>
          <p className={styles.paragraph}>Certain features require payment or use of credits.</p>
          <ul className={styles.list}>
            <li className={styles.listItem}>Prices and credit costs may change.</li>
            <li className={styles.listItem}>Credits are non-refundable unless required by law.</li>
            <li className={styles.listItem}>You are responsible for all applicable taxes.</li>
          </ul>
          <p className={styles.paragraph}>
            Failure to pay may result in suspension or termination of access.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>8. Third-Party Services</h2>
          <p className={styles.paragraph}>
            The Services may integrate with third-party tools or platforms. aether studio is not responsible for third-party services and does not endorse them.
          </p>
          <p className={styles.paragraph}>
            Your use of third-party services is governed by their own terms and policies.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>9. Termination</h2>
          <p className={styles.paragraph}>
            You may stop using the Services at any time.
          </p>
          <p className={styles.paragraph}>
            We may suspend or terminate your access if you violate these Terms or if required for legal or security reasons.
          </p>
          <p className={styles.paragraph}>
            Upon termination, your license to use the Services ends. Certain provisions will survive termination.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>10. Disclaimers</h2>
          <p className={styles.paragraph} style={{ textTransform: 'uppercase' }}>
            THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY LAW, AETHER STUDIO DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>11. Limitation of Liability</h2>
          <p className={styles.paragraph} style={{ textTransform: 'uppercase' }}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, AETHER STUDIO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES.
          </p>
          <p className={styles.paragraph} style={{ textTransform: 'uppercase' }}>
            OUR TOTAL LIABILITY ARISING OUT OF OR RELATED TO THE SERVICES SHALL NOT EXCEED THE AMOUNT YOU PAID TO AETHER STUDIO IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>12. Indemnification</h2>
          <p className={styles.paragraph}>
            You agree to indemnify and hold harmless aether studio and its affiliates from any claims, damages, liabilities, and expenses arising out of your use of the Services or violation of these Terms.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>13. Governing Law and Dispute Resolution</h2>
          <p className={styles.paragraph}>
            These Terms are governed by the laws of [Insert Jurisdiction], without regard to conflict of law principles.
          </p>
          <p className={styles.paragraph}>
            Any disputes shall be resolved exclusively in the courts located in [Insert Venue], unless otherwise required by law.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>14. Changes to These Terms</h2>
          <p className={styles.paragraph}>
            We may update these Terms from time to time. Continued use of the Services after changes become effective constitutes acceptance of the updated Terms.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>15. Contact Information</h2>
          <p className={styles.paragraph}>
            For questions about these Terms, contact us at:
          </p>
          <p className={styles.paragraph}>
            aether studio<br />
            Email: legal@aether.studio
          </p>
        </section>

        <hr style={{ margin: '2rem 0', border: 'none', borderTop: '1px solid var(--color-border)' }} />

        <p className={styles.paragraph} style={{ fontSize: 'var(--font-size-sm)', fontStyle: 'italic' }}>
          These Terms of Service are provided for general informational purposes and do not constitute legal advice.
        </p>
      </div>
    </MarketingLayout>
  )
}
